<template>
  <div v-if="userHasPermission(constants.PERMISSION_TYPES.PUDO_EXPIRY, constants.PERMISSION_VALUES.LIST)">
    <main-header />
    <pudo-filter
      v-model="pudoFilter"
      :use-date="false" />
    <div class="spl-expired-parcels-table__buttons">
      <form-button
        v-if="selectedParcels.length > 0"
        data-cy="expiry-set-action"
        @onClick="openActionModalForSelectedParcels">Set expiry action</form-button>
      <form-button
        @onClick="exportExcel">Export Excel</form-button>
    </div>
    <div v-if="!isLoading" class="spl-expired-parcels-table">
      <table>
        <thead>
          <tr class="spl-expired-parcels-table__head">
            <th v-if="userHasPermission(constants.PERMISSION_TYPES.PUDO_EXPIRY, constants.PERMISSION_VALUES.CREATE)" class="spl-parcel-table__head--checkbox">
              <form-checkbox
                :checked="allParcelsSelected"
                @checkboxChanged="checkAllParcels" />
            </th>
            <th
              v-for="(header, index) in tableFields"
              :key="'dashboardPrecariageHeader' + index"
              @click="toggleSort(header.name)">
              <span
                v-if="findSort(sortedFields, header.name)"
                :class="findSort(sortedFields, header.name).direction"
                class="order-arrow" />
              {{ header.label }}
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(expiredParcel, parcelIndex) in expiredParcels" :key="'expiredParcelTable' + parcelIndex">
            <td v-if="userHasPermission(constants.PERMISSION_TYPES.PUDO_EXPIRY, constants.PERMISSION_VALUES.CREATE)" class="spl-parcel-table__field--checkbox">
              <form-checkbox
                :checked="selectedParcels.indexOf(expiredParcel.Parcel_ID) !== -1"
                @checkboxChanged="checkParcel(expiredParcel)" />
            </td>
            <td
              v-for="(field, fieldIndex) in tableFields"
              :key="'dashboardPrecariageField' + parcelIndex + '.' + fieldIndex"
              :class="{'spl-expired-parcels-table__field--number': isNumber(expiredParcel[field.name]),
                       'spl-expired-parcels-table__field--long': field.long,
                       'spl-expired-parcels-table__field--bold': field.bold,
                       'spl-expired-parcels-table__field--warning': isCollectBeforeTomorrow(field.name, expiredParcel[field.name])}"
              @click="openActionModalForParcel(expiredParcel)">
              <span>
                {{ formatField(expiredParcel[field.name], field.name, field.type) }}
              </span>
            </td></tr>
        </tbody>
      </table>
    </div>
    <pudo-expiry-action-modal
      ref="expiryActionModal"
      @expiryActionSaving="startLoader()"
      @expiryActionSaved="expirySaved()" />
    <div v-if="isLoading" class="spl-load-img" />
  </div>
</template>

<script>
import { mapActions, mapState, mapMutations } from 'vuex'
import { find, findIndex, orderBy, isNumber, pull } from 'lodash'
import Helpers from '@/helpers.vue'
import constants from '@/constants'
import moment from 'moment'

export default {
  mixins: [Helpers],
  data () {
    return {
      isLoading: false,
      pudoFilter: {},
      sortedFields: [],
      allParcelsSelected: false,
      selectedParcels: [],
      tableFields: [
        { name: 'Customer_Name', label: 'Customer', type: 'String' },
        { name: 'Country_Name', label: 'Country', type: 'String' },
        { name: 'Booked_Pickup_Date', label: 'Booked Date', type: 'Date' },
        { name: 'Delivery_Timestamp', label: 'Delivered', type: 'Time' },
        { name: 'Latest_Expiry_Date', label: 'Eng. To Collect Before', type: 'Time' },
        { name: 'COUNTER_NUMBER', label: 'Counter Number', type: 'String' },
        { name: 'ORDER_NUMBER', label: 'Order Number', type: 'String' },
        { name: 'DELIVERY_NOTE_NUMBER', label: 'Delivery Note Number', type: 'String' },
        { name: 'SERVICE_ORDER', label: 'Service Order', type: 'String' },
        { name: 'Delivery_Address_Name', label: 'Box Name', type: 'String' },
        { name: 'Delivery_Address_Street', label: 'Box Address', type: 'String' },
        { name: 'Delivery_Address_Postal_Code', label: 'Box Postal Code', type: 'String' },
        { name: 'Delivery_Address_City', label: 'Box City', type: 'String' },
        { name: 'Weight', label: 'Weight', type: 'float' }
      ],
      constants
    }
  },
  computed: {
    ...mapState('pudo', ['expiredParcels'])
  },
  watch: {
    pudoFilter: {
      handler: function () {
        this.isLoading = true
        this.loadExpiredParcels(this.pudoFilter).then(() => {
          this.isLoading = false
        }).catch(() => {
          this.isLoading = false
        })
        this.loadExpiryOptions(this.pudoFilter)
      },
      deep: true
    }
  },
  mounted () {
  },
  methods: {
    ...mapActions('pudo', ['loadExpiredParcels', 'loadExpiryOptions']),
    ...mapMutations('pudo', ['setSelectedExpiredParcels']),

    startLoader () {
      this.isLoading = true
    },

    expirySaved () {
      this.loadExpiredParcels(this.pudoFilter).then(() => {
        // clear selection
        this.selectedParcels = []
        this.isLoading = false
      }).catch(() => {
        this.isLoading = false
      })
    },

    openActionModalForParcel (expiredParcel) {
      if (this.userHasPermission(constants.PERMISSION_TYPES.PUDO_EXPIRY, constants.PERMISSION_VALUES.CREATE)) {
        this.setSelectedExpiredParcels([expiredParcel.Parcel_ID])
        this.$refs.expiryActionModal.openModal()
      }
    },

    openActionModalForSelectedParcels () {
      if (this.userHasPermission(constants.PERMISSION_TYPES.PUDO_EXPIRY, constants.PERMISSION_VALUES.CREATE)) {
        this.setSelectedExpiredParcels(this.selectedParcels)
        this.$refs.expiryActionModal.openModal()
      }
    },

    checkAllParcels (checked) {
      this.selectedParcels = []
      if (checked) {
        for (let i = 0; i < this.expiredParcels.length; i++) {
          this.selectedParcels.push(this.expiredParcels[i].Parcel_ID)
        }
      }
    },

    checkParcel (parcel) {
      if (this.selectedParcels.indexOf(parcel.Parcel_ID) !== -1) {
        pull(this.selectedParcels, parcel.Parcel_ID)
      } else {
        this.selectedParcels.push(parcel.Parcel_ID)
      }
    },

    exportExcel () {
      var excelList = {
        Columns: [],
        Rows: []
      }
      var selectedFields = this.tableFields
      // Loop through the columns and add them to excel object
      for (let i = 0; i < selectedFields.length; i++) {
        const field = selectedFields[i]
        excelList.Columns.push({ ID: field.name, Header: field.label, Type: this.getColumnType(field.type) })
      }

      const results = this.expiredParcels
      // add the values
      for (let i = 0; i < results.length; i++) {
        var rowValues = { Value: [] }
        const result = results[i]
        for (let j = 0; j < selectedFields.length; j++) {
          const field = selectedFields[j]
          rowValues.Value.push(result[field.name])
        }
        excelList.Rows.push(rowValues)
      }
      // Call the excel download helper
      this.getexcelDownload(excelList, 'Expiry_History.xlsx')
    },

    getColumnType (fieldType) {
      // Converts the formats into excel compatible ones
      // "Number", "DateTime", "Text", "Boolean"
      if (fieldType) {
        switch (fieldType) {
          case 'Date':
          case 'Time':
            return 'DateTime'

          case 'String':
          default: {
            return 'Text'
          }
        }
      }
      return ''
    },

    toggleSort (field) {
      // Finds the sorted field in the array
      var sortedField = find(this.sortedFields, { field: field })
      if (sortedField) {
        // Switches to desc when asc already selected
        if (sortedField.direction === 'asc') {
          sortedField.direction = 'desc'
        } else {
          // removes the sorting when desc was selected
          this.sortedFields.splice(findIndex(this.sortedFields, { field: field }))
        }
      } else {
        // adds the field when not yet present
        this.sortedFields.push({ direction: 'asc', field: field })
      }
      this.sortData()
    },

    // Sorting the data
    sortData () {
      // Sort fields and directions seperate for lodash orderBy method
      let sortFields = []
      let sortDirections = []
      // Create the sort fields
      for (let i = 0; i < this.sortedFields.length; i++) {
        const sortElement = this.sortedFields[i]
        sortFields.push(sortElement.field)
        sortDirections.push(sortElement.direction)
      }
      // Default sorting depending on grouping
      if (sortFields.length === 0) {
        sortFields = [constants.REFERENCE_TYPE_NAME.COUNTER_NUMBER]
        sortDirections = ['asc']
      }
      // Use the lodash orderBy method for sorting
      this.reportList = orderBy(this.reportList, sortFields, sortDirections)
    },

    // Special formatting for fields
    formatField (field, name, type) {
      if (type === 'Date') {
        return this.formatDate(field, false, 'YYYY-MM-DD')
      } else if (type === 'Time') {
        return this.formatDate(field, true, 'YYYY-MM-DD HH:mm')
      }
      // empty fields will be removed
      if (field == null || field === 0) {
        return ''
      }
      return field
    },

    // find the right sort value for displaying arrows
    findSort (array, value) {
      return find(array, { field: value })
    },

    // including of helper since lodash not available in vue template
    isNumber (value) {
      return isNumber(value)
    },
    isCollectBeforeTomorrow (fieldName, value) {
      if (fieldName === 'Latest_Expiry_Date') {
        var tomorrow = moment().add(1, 'days').format('YYYY-MM-DDT00:00:00')
        return value <= tomorrow
      } else {
        return false
      }
    }
  }
}
</script>
<style lang="scss">
  @import '../../variables.scss';
  .spl-expired-parcels-table {

    &__buttons {
      margin-left: 70px !important;
      margin-top: 10px !important;
    }

    margin: 0 70px;
    width: calc(100% - 138px);
    height: calc(100vh - 245px);
    overflow: auto;

    table {
      border-spacing: 0;
      table-layout: fixed;
    }
    &__head {
      th {
        box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.2), 0 3px 5px 0 rgba(0, 0, 0, 0.1);
      }
      &--sticky {
        position: sticky;
        left: 41px;
        z-index: 3 !important;
      }
      &--checkbox {
        .spl-checkbox {
          margin: 10px 2px 0 6px;
        }
        position: sticky;
        left: 0px;
        z-index: 3 !important;
      }
    }
    &__row {
      &--selected {
        background-color: $tableRowBackgroundColorSelected !important;
      }
      &--deleted {
        opacity: 0.3;
        background-color: lightgrey !important;
      }
    }
    &__field {
      &--number {
        text-align: right;
        width: 70px;
      }
      &--sticky {
        position: sticky;
        left: 41px;
        box-shadow: 3px 0px 3px 0px rgba(0, 0, 0, 0.2);
        background-color: $tableHeaderBackgroundColor;
      }
      &--checkbox {
        .spl-checkbox {
          margin: 0 2px 0 6px;
        }
        position: sticky;
        background-color: $tableHeaderBackgroundColor;
        left: 0;
      }
      &--long {
        max-width: 400px !important;
        white-space: nowrap;
      }
      &--success {
        border-bottom: 2px solid $green !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $green;
      }
      &--warning {
        border-bottom: 2px solid $darkYellow !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $darkYellow;
      }
      &--error {
        border-bottom: 2px solid $red !important;
        padding-bottom: 3px !important;
        font-weight: 600;
        color: $red;
      }
      &--bold {
        font-weight: 600;
      }
      &--empty:hover {
        background-color: $tableRowBackgroundColorHover !important;
      }
    }
    thead {
      th {
        position: sticky;
        top: 0;
        text-align: left;
        font-family: $mono;
        font-size: 9px;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1px;
        line-height: 12px;
        background-color: $tableHeaderBackgroundColor;
        padding: 0px 5px  0px 5px;
        height: 40px;
        border-bottom: 1px solid $tableHeaderBorderColor;
      }

      th:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }
    }
    tbody {

      tr {
        background-color: $tableRowBackgroundColor;
      }

      tr:hover {
        background-color: $tableRowBackgroundColorHover;
      }

      td {
        border-style: solid;
        border-width: 0 1px 1px 0;
        border-color: $tableRowBorderLowColor;
        border-bottom-color: $tableRowBorderColor;
        font-size: 12px;
        padding: 5px;
        padding-top: 8px;
        white-space: nowrap;
      }

      td:hover {
        background-color: $tableColumnBackgroundColorHover;
        cursor: pointer;
      }

      td:first-child {
        border-left-width: 1px;
      }
    }
}
</style>
